/**
 * Per-recipient notify channel overrides for compose and thread quick-reply.
 * Saved map is sent as notify_channels JSON; Cancel / new draft resets to preference defaults.
 */
import axios from "axios";
import { CURR_API } from "@/constants/apis";
import {
  buildNotifyChannelMap,
  normalizeNotifyChannel,
  MESSAGE_NOTIFY_AVAILABLE,
} from "@/utils/notifyChannels";

export default {
  data() {
    return {
      showNotifyChannelsModal: false,
      notifyChannelsLoading: false,
      // unique_ID → communication_preference (from account_user)
      notifyRecipientPrefs: {},
      // unique_ID → channel saved for this draft only
      notifyChannelOverrides: {},
      // Working copy while the modal is open
      notifyChannelDraft: {},
    };
  },

  watch: {
    selectedRecipients: {
      handler(list) {
        if (this.activeView === "compose") {
          this.pruneNotifyChannelState(list);
        }
      },
      deep: true,
    },
    selectedThread(thread, prev) {
      // Only reset when switching conversations, not when the same thread object is refreshed.
      const nextId = thread && thread.id;
      const prevId = prev && prev.id;
      if (nextId === prevId) return;
      this.resetNotifyChannelState();
    },
  },

  computed: {
    /**
     * Whether the widgets offer "Notify recipients" and "Select channels" at
     * all. Off until the port can send the alerts (utils/notifyChannels.js
     * MESSAGE_NOTIFY_AVAILABLE, NT-11): a box the server would ignore tells
     * the sender their recipients were alerted when nobody was.
     */
    notifyAvailable() {
      return MESSAGE_NOTIFY_AVAILABLE;
    },

    // Recipients to show in the channel modal / send as overrides.
    notifyChannelTargetRecipients() {
      if (this.activeView === "thread" && this.selectedThread) {
        return this.threadNotifyRecipients;
      }
      return this.selectedRecipients || [];
    },

    threadNotifyRecipients() {
      const thread = this.selectedThread;
      if (!thread) return [];
      const selfId = String(
        (this.messagingIdentity && this.messagingIdentity.subAccountNumber) || ""
      ).trim();

      if (thread.isGroup && Array.isArray(thread.participants)) {
        // Prefer id+name pairs built with the thread (same length / same order).
        // Do not zip participants[] with otherParticipantNames[] — those lists
        // exclude/dedupe differently and can assign the wrong channel to a person.
        if (Array.isArray(thread.otherParticipants)) {
          return thread.otherParticipants
            .map((p) => {
              const uid = String(
                (p && (p.id || p.subaccountnumber)) || ""
              ).trim();
              if (!uid || uid === selfId) return null;
              return {
                subaccountnumber: uid,
                name: (p && p.name) || uid,
              };
            })
            .filter(Boolean);
        }
        return thread.participants
          .map((id) => String(id || "").trim())
          .filter((id) => id && id !== selfId)
          .map((id) => ({
            subaccountnumber: id,
            name: id,
          }));
      }

      const uid = String(thread.otherParticipantId || "").trim();
      if (!uid) return [];
      return [
        {
          subaccountnumber: uid,
          name: thread.otherParticipant || uid,
        },
      ];
    },

    notifyChannelModalRows() {
      return (this.notifyChannelTargetRecipients || [])
        .map((r) => {
          const uid = String(r.subaccountnumber || r.id || "").trim();
          if (!uid) return null;
          return {
            uid,
            name: r.name || uid,
          };
        })
        .filter(Boolean);
    },
  },

  methods: {
    recipientUid(recipient) {
      return String(
        (recipient && (recipient.subaccountnumber || recipient.id)) || ""
      ).trim();
    },

    pruneNotifyChannelState(list) {
      const keep = {};
      (list || []).forEach((r) => {
        const uid = this.recipientUid(r);
        if (uid) keep[uid] = true;
      });
      const pruneMap = (src) => {
        const next = {};
        Object.keys(src || {}).forEach((uid) => {
          if (keep[uid]) next[uid] = src[uid];
        });
        return next;
      };
      this.notifyChannelOverrides = pruneMap(this.notifyChannelOverrides);
      this.notifyRecipientPrefs = pruneMap(this.notifyRecipientPrefs);
      this.notifyChannelDraft = pruneMap(this.notifyChannelDraft);
    },

    resetNotifyChannelState() {
      this.showNotifyChannelsModal = false;
      this.notifyChannelsLoading = false;
      this.notifyRecipientPrefs = {};
      this.notifyChannelOverrides = {};
      this.notifyChannelDraft = {};
    },

    async openNotifyChannelsModal() {
      const targets = this.notifyChannelTargetRecipients || [];
      if (!targets.length) {
        alert("Add recipients before selecting channels.");
        return;
      }
      this.showNotifyChannelsModal = true;
      await this.loadNotifyRecipientPrefs();
      this.seedNotifyChannelDraft();
    },

    async loadNotifyRecipientPrefs() {
      const ids = (this.notifyChannelTargetRecipients || [])
        .map((r) => this.recipientUid(r))
        .filter(Boolean);
      if (!ids.length || !this.messagingIdentity || !this.messagingIdentity.accountNumber) {
        return;
      }
      this.notifyChannelsLoading = true;
      try {
        const content = {
          Messages: {
            API_Meth: "GetAccountUsersContactInfoByUniqueIds",
            accountnumber: this.messagingIdentity.accountNumber,
            unique_IDs: ids,
          },
        };
        const response = await axios.post(CURR_API + "/tswebhook.php", content, {
          headers: { "Content-Type": "application/json;" },
        });
        const rows =
          response.data &&
          String(response.data.status).toLowerCase() === "successful" &&
          Array.isArray(response.data.data)
            ? response.data.data
            : [];
        const prefs = { ...this.notifyRecipientPrefs };
        rows.forEach((row) => {
          const uid = String(row.unique_ID || "").trim();
          if (uid) {
            prefs[uid] = row.communication_preference || "";
          }
        });
        this.notifyRecipientPrefs = prefs;
      } catch (error) {
        console.error("Error loading notify preferences:", error);
      } finally {
        this.notifyChannelsLoading = false;
      }
    },

    seedNotifyChannelDraft() {
      // Saved overrides win; otherwise each recipient's preferred channel.
      this.notifyChannelDraft = buildNotifyChannelMap(
        this.notifyChannelTargetRecipients,
        this.notifyChannelOverrides,
        this.notifyRecipientPrefs
      );
    },

    onNotifyChannelDraftUpdate({ uid, channel }) {
      if (!uid) return;
      this.notifyChannelDraft = {
        ...this.notifyChannelDraft,
        [uid]: normalizeNotifyChannel(channel),
      };
    },

    saveNotifyChannels(draft) {
      this.notifyChannelOverrides = { ...(draft || {}) };
      this.showNotifyChannelsModal = false;
      this.notifyChannelDraft = {};
    },

    cancelNotifyChannels() {
      // Discard draft; overrides (if any) stay until Save on a later open, or reset.
      this.showNotifyChannelsModal = false;
      this.notifyChannelDraft = {};
    },

    /**
     * Append notify_channels for this send.
     * Only explicit Save overrides are sent (email|sms|phone|all); missing ids
     * keep communication_preference on the backend. "all" expands server-side.
     */
    appendNotifyChannels(formData, recipientsSubset) {
      if (!this.notifyRecipients) return;
      const subset = Array.isArray(recipientsSubset)
        ? recipientsSubset
        : this.notifyChannelTargetRecipients;
      const map = {};
      (subset || []).forEach((r) => {
        const uid = this.recipientUid(r);
        if (!uid || !this.notifyChannelOverrides[uid]) return;
        map[uid] = normalizeNotifyChannel(this.notifyChannelOverrides[uid]);
      });
      if (Object.keys(map).length > 0) {
        formData.append("notify_channels", JSON.stringify(map));
      }
    },
  },
};
